import {createStyles} from '@mantine/core';
import React, {ReactNode, useCallback, useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

const LEFT_PANEL_SIZE = SidepanelSize.SMALL;

export interface LeftPanelView {
  icon: BenchlingIconName;
  tooltip: string;
  name: string;
  content: ReactNode;
}

interface ProcedureRunDesignEditorLeftSidepanelProps {
  /** The left panel views, one per rail icon. The first is selected by default. */
  leftPanelViews: Array<LeftPanelView>;
  /** When the editor enters edit mode, the panel opens on the current view, defaulting to the first. */
  isInEditState: boolean;
}

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const {classes} = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);

  const handleSelectedIconIndexChange = useCallback((index: number | null) => {
    setSelectedIconIndex(index);
    setIsOpen(index != null);
  }, []);

  useEffect(() => {
    if (!props.isInEditState) {
      return;
    }

    setIsOpen(true);
    setSelectedIconIndex((prev) => prev ?? 0);
  }, [props.isInEditState]);

  const selectedView = props.leftPanelViews[selectedIconIndex ?? 0];
  assert(selectedView != null, 'Selected left panel view is out of range');

  return (
    <>
      <FlowchartSidebar
        icons={props.leftPanelViews.map((view) => ({
          name: view.icon,
          tooltip: view.tooltip,
          onClick: () => {
            setIsOpen(true);
          },
        }))}
        selectedIconIndex={selectedIconIndex}
        setSelectedIconIndex={handleSelectedIconIndexChange}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        className={classes.leftPanel}
        sidepanelName={selectedView.name}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedIconIndex(null);
        }}
        size={LEFT_PANEL_SIZE}
        showExpandCollapseButtons={false}
      >
        {selectedView.content}
      </FlowchartContextPanel>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  leftPanel: {
    color: theme.colors.gray[9],
    minHeight: 0,
  },
}));
